/**
 * Мост между панелью свойств и открытым полем набора текста.
 *
 * Когда внутри надписи выделен кусок, кнопки «Ж», «К», цвет и размер должны
 * менять только его. Панель живёт отдельно от поля набора, поэтому она
 * сначала спрашивает поле: «есть выделенный кусок — примени к нему». Если
 * поле не открыто или выделения нет, панель меняет оформление всей надписи,
 * как раньше.
 *
 * Команда, а не готовый стиль: жирность/курсив у куска переключаются
 * относительно самого куска, а не относительно всей надписи.
 */

export type TextMarkCommand =
  | { command: "bold" }
  | { command: "italic" }
  | { command: "underline" }
  | { command: "strike" }
  | { command: "color"; value: string }
  | { command: "fontSize"; value: number }
  /** Маркер под выделенным куском; null — снять. */
  | { command: "highlight"; value: string | null }
  /** Строки под курсором или выделением — в список (или из списка). */
  | { command: "list"; value: "bullet" | "number" }
  /** Вложить пункт глубже или вынести уровнем выше. */
  | { command: "indent"; value: 1 | -1 }
  /** Ссылка на выделенный кусок (или на ссылку под курсором); null — снять. */
  | { command: "link"; value: string | null };

export type TextSelectionFormatter = (cmd: TextMarkCommand) => boolean;

/** Что сейчас под курсором в поле набора — для окошка ссылки. */
export type TextSelectionLinkInfo = {
  /** Выделен кусок или курсор стоит в ссылке — есть к чему её применить. */
  canLink: boolean;
  /** Ссылка, которая уже стоит на этом месте. */
  href: string | null;
};

let formatter: TextSelectionFormatter | null = null;
let linkInfoReader: (() => TextSelectionLinkInfo | null) | null = null;

/** Поле набора отдаёт сведения о ссылке под курсором. */
export function registerTextSelectionLinkInfo(
  fn: () => TextSelectionLinkInfo | null
): () => void {
  linkInfoReader = fn;
  return () => {
    if (linkInfoReader === fn) linkInfoReader = null;
  };
}

/** null — поле набора не открыто. */
export function readTextSelectionLinkInfo(): TextSelectionLinkInfo | null {
  if (!linkInfoReader) return null;
  try {
    return linkInfoReader();
  } catch {
    return null;
  }
}

/** Ctrl+K в поле набора — открыть окошко ссылки на панели. */
export const TEXT_LINK_REQUEST_EVENT = "wb:text-link-request";

/** Поле набора регистрирует себя на время правки. */
export function registerTextSelectionFormatter(fn: TextSelectionFormatter): () => void {
  formatter = fn;
  return () => {
    if (formatter === fn) formatter = null;
  };
}

/** true — оформление применено к выделенному куску, объект трогать не нужно. */
export function applyMarkToTextSelection(cmd: TextMarkCommand): boolean {
  if (!formatter) return false;
  try {
    return formatter(cmd);
  } catch {
    return false;
  }
}
